import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ReactFlow,
  ReactFlowProvider,
  Background, 
  Controls, 
  addEdge, 
  Node, 
  Edge, 
  Connection,
  NodeTypes,
  Handle,
  Position,
  applyNodeChanges,
  applyEdgeChanges,
  OnNodesChange,
  OnEdgesChange,
  NodeChange,
  EdgeChange
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { getTemplatesList, getTemplateCounts, getPhoneGroups, saveFlow as saveFlowAPI, getFlowById, refreshToken } from '../../../api/login.service';

interface Template {
  id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  components?: Array<{
    type: string;
    text?: string;
    format?: string;
    example?: any;
  }>;
  inputCount?: number; // Number of inputs this template expects
  outputCount?: number; // Number of outputs this template provides
  inputLabels?: string[]; // Labels for inputs
  outputLabels?: string[]; // Labels for outputs
}

// Dummy labels - will come dynamically from API
const DUMMY_LABELS = [
  { id: 'label_1', name: 'Customer Name', value: 'Customer Name' },
  { id: 'label_2', name: 'Order ID', value: 'Order ID' },
  { id: 'label_3', name: 'Product Name', value: 'Product Name' },
  { id: 'label_4', name: 'Price', value: 'Price' },
  { id: 'label_5', name: 'Date', value: 'Date' },
  { id: 'label_6', name: 'Status', value: 'Status' },
  { id: 'label_7', name: 'Phone Number', value: 'Phone Number' },
  { id: 'label_8', name: 'Email', value: 'Email' },
  { id: 'label_9', name: 'Address', value: 'Address' },
  { id: 'label_10', name: 'Payment Method', value: 'Payment Method' },
  { id: 'label_11', name: 'Delivery Date', value: 'Delivery Date' },
  { id: 'label_12', name: 'Tracking Number', value: 'Tracking Number' },
];

interface PhoneGroup {
  id: string;
  name: string;
  description?: string;
  totalCount?: number;
}

interface FlowNodeData extends Record<string, unknown> {
  label: string;
  type: 'start' | 'template' | 'text' | 'wait_reply' | 'end' | 'create_ticket' | 'add_to_group' | 'webhook' | 'http_request';
  templateId?: string;
  templateName?: string;
  variables?: Record<string, string>;
  textContent?: string;
  yesTemplate?: string;
  noTemplate?: string;
  selectedGroupId?: string;
  selectedGroupName?: string;
  inputCount?: number; // Number of input handles
  outputCount?: number; // Number of output handles
  inputLabels?: string[]; // Labels for input handles
  outputLabels?: string[]; // Labels for output handles
  webhookUrl?: string; // Webhook URL
  httpMethod?: string; // HTTP method (GET, POST, PUT, DELETE, etc.)
  httpUrl?: string; // HTTP request URL
  httpHeaders?: string; // JSON string for headers
  httpBody?: string; // Request body
}

// Custom Node Components
const StartNode = ({ data }: { data: FlowNodeData }) => (
  <div className="px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg shadow-md">
    <Handle type="source" position={Position.Right} style={{ background: '#6b7280', width: '10px', height: '10px', border: '2px solid white' }} />
    <div className="font-semibold text-sm text-gray-800">{data.label}</div>
  </div>
);

const TemplateNode = ({ data }: { data: FlowNodeData }) => {
  // Support multiple inputs and outputs
  const inputCount = data.inputCount || 1;
  const outputCount = data.outputCount || 1;
  const inputLabels = data.inputLabels || [];
  const outputLabels = data.outputLabels || [];
  
  // Calculate handle positions - evenly spaced
  const getHandlePosition = (index: number, total: number) => {
    if (total === 1) return '50%';
    // Space handles evenly from 20% to 80% of node height
    const startPercent = 20;
    const endPercent = 80;
    const step = (endPercent - startPercent) / (total - 1);
    return `${startPercent + (index * step)}%`;
  };
  
  return (
    <div className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg shadow-md min-w-[180px] relative">
      {/* Multiple input handles with labels */}
      {Array.from({ length: inputCount }).map((_, index) => {
        const position = getHandlePosition(index, inputCount);
        return (
          <React.Fragment key={`input-fragment-${index}`}>
          <Handle
            type="target"
            position={Position.Left}
            id={`input-${index}`}
            style={{ 
                top: position,
              background: '#6b7280',
                width: '12px',
                height: '12px',
                border: '2px solid white',
                borderRadius: '50%'
            }}
          />
          {inputLabels[index] && (
            <div 
                className="absolute left-[-90px] text-xs text-gray-600 whitespace-nowrap bg-white px-1.5 py-0.5 rounded shadow-sm border border-gray-200"
                style={{ top: position, transform: 'translateY(-50%)' }}
            >
              {inputLabels[index]}
            </div>
          )}
          </React.Fragment>
        );
      })}
      <div className="font-semibold text-sm text-gray-800 mb-1">{data.label}</div>
      {data.templateName && (
        <div className="text-xs text-gray-600 truncate max-w-[160px]">{data.templateName}</div>
      )}
      {/* Multiple output handles with labels */}
      {Array.from({ length: outputCount }).map((_, index) => {
        const position = getHandlePosition(index, outputCount);
        return (
          <React.Fragment key={`output-fragment-${index}`}>
          <Handle
            type="source"
            position={Position.Right}
            id={`output-${index}`}
            style={{ 
                top: position,
              background: '#6b7280',
                width: '12px',
                height: '12px',
                border: '2px solid white',
                borderRadius: '50%'
            }}
          />
          {outputLabels[index] && (
            <div 
                className="absolute right-[-110px] text-xs text-gray-600 whitespace-nowrap bg-white px-1.5 py-0.5 rounded shadow-sm border border-gray-200"
                style={{ top: position, transform: 'translateY(-50%)' }}
            >
              {outputLabels[index]}
            </div>
          )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const TextNode = ({ data }: { data: FlowNodeData }) => (
  <div className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg shadow-md min-w-[200px]">
    <Handle type="target" position={Position.Left} style={{ background: '#6b7280', width: '10px', height: '10px', border: '2px solid white' }} />
    <div className="font-semibold mb-1 text-gray-800">{data.label}</div>
    {data.textContent && (
      <div className="text-xs text-gray-600 truncate max-w-[180px]">
        {data.textContent.substring(0, 50)}...
      </div>
    )}
    <Handle type="source" position={Position.Right} style={{ background: '#6b7280', width: '10px', height: '10px', border: '2px solid white' }} />
  </div>
);

const WaitReplyNode = ({ data }: { data: FlowNodeData }) => (
  <div className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg shadow-md min-w-[200px]">
    <Handle type="target" position={Position.Left} style={{ background: '#6b7280', width: '10px', height: '10px', border: '2px solid white' }} />
    <div className="font-semibold mb-1 text-gray-800">{data.label}</div>
    <div className="text-xs text-gray-600">
      Yes → {data.yesTemplate || 'Not set'}
    </div>
    <div className="text-xs text-gray-600">
      No → {data.noTemplate || 'Not set'}
    </div>
    <Handle type="source" position={Position.Right} id="yes" style={{ top: '30%', background: '#6b7280', width: '10px', height: '10px', border: '2px solid white' }} />
    <Handle type="source" position={Position.Right} id="no" style={{ top: '70%', background: '#6b7280', width: '10px', height: '10px', border: '2px solid white' }} />
  </div>
);

const CreateTicketNode = ({ data }: { data: FlowNodeData }) => (
  <div className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg shadow-md min-w-[200px]">
    <Handle type="target" position={Position.Left} style={{ background: '#6b7280', width: '10px', height: '10px', border: '2px solid white' }} />
    <div className="font-semibold mb-1 text-gray-800">{data.label}</div>
    <div className="text-xs text-gray-600">Create Ticket in CRM</div>
    <Handle type="source" position={Position.Right} style={{ background: '#6b7280', width: '10px', height: '10px', border: '2px solid white' }} />
  </div>
);

const AddToGroupNode = ({ data }: { data: FlowNodeData }) => (
  <div className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg shadow-md min-w-[200px]">
    <Handle type="target" position={Position.Left} style={{ background: '#6b7280', width: '10px', height: '10px', border: '2px solid white' }} />
    <div className="font-semibold mb-1 text-gray-800">{data.label}</div>
    {data.selectedGroupName && (
      <div className="text-xs text-gray-600">Group: {data.selectedGroupName}</div>
    )}
    <Handle type="source" position={Position.Right} style={{ background: '#6b7280', width: '10px', height: '10px', border: '2px solid white' }} />
  </div>
);

const WebhookNode = ({ data }: { data: FlowNodeData }) => {
  const inputCount = data.inputCount || 1;
  const outputCount = data.outputCount || 1;
  
  return (
    <div className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg shadow-md min-w-[180px]">
      {/* Multiple input handles */}
      {Array.from({ length: inputCount }).map((_, index) => (
        <Handle
          key={`input-${index}`}
          type="target"
          position={Position.Left}
          id={`input-${index}`}
          style={{ 
            top: `${(index + 1) * (100 / (inputCount + 1))}%`,
            background: '#6b7280',
            width: '10px',
            height: '10px',
            border: '2px solid white'
          }}
        />
      ))}
      <div className="font-semibold text-sm text-gray-800">{data.label}</div>
      {data.webhookUrl && (
        <div className="text-xs text-gray-600 truncate max-w-[160px]">{data.webhookUrl}</div>
      )}
      {/* Multiple output handles */}
      {Array.from({ length: outputCount }).map((_, index) => (
        <Handle
          key={`output-${index}`}
          type="source"
          position={Position.Right}
          id={`output-${index}`}
          style={{ 
            top: `${(index + 1) * (100 / (outputCount + 1))}%`,
            background: '#6b7280',
            width: '10px',
            height: '10px',
            border: '2px solid white'
          }}
        />
      ))}
    </div>
  );
};

const HttpRequestNode = ({ data }: { data: FlowNodeData }) => {
  const inputCount = data.inputCount || 1;
  const outputCount = data.outputCount || 1;
  
  return (
    <div className="px-3 py-1.5 bg-orange-500 text-white rounded-lg shadow-lg">
      {/* Multiple input handles */}
      {Array.from({ length: inputCount }).map((_, index) => (
        <Handle
          key={`input-${index}`}
          type="target"
          position={Position.Left}
          id={`input-${index}`}
          style={{ 
            top: `${(index + 1) * (100 / (inputCount + 1))}%`,
            background: '#fff',
            width: '8px',
            height: '8px'
          }}
        />
      ))}
      <div className="font-semibold text-sm">{data.label}</div>
      {data.httpMethod && data.httpUrl && (
        <div className="text-xs opacity-90 truncate max-w-[120px]">
          {data.httpMethod} {data.httpUrl.substring(0, 20)}...
        </div>
      )}
      {/* Multiple output handles */}
      {Array.from({ length: outputCount }).map((_, index) => (
        <Handle
          key={`output-${index}`}
          type="source"
          position={Position.Right}
          id={`output-${index}`}
          style={{ 
            top: `${(index + 1) * (100 / (outputCount + 1))}%`,
            background: '#fff',
            width: '8px',
            height: '8px'
          }}
        />
      ))}
    </div>
  );
};

const EndNode = ({ data }: { data: FlowNodeData }) => (
  <div className="px-4 py-2 bg-red-500 text-white rounded-lg shadow-lg">
    <Handle type="target" position={Position.Left} />
    <div className="font-semibold">{data.label}</div>
  </div>
);

const nodeTypes: NodeTypes = {
  start: StartNode,
  template: TemplateNode,
  text: TextNode,
  wait_reply: WaitReplyNode,
  create_ticket: CreateTicketNode,
  add_to_group: AddToGroupNode,
  webhook: WebhookNode,
  http_request: HttpRequestNode,
  end: EndNode,
};

export default function AutomatedFlowBuilder() {
  const navigate = useNavigate();
  const wabaId = sessionStorage.getItem('wabaId');
  const editingFlowId = sessionStorage.getItem('editingFlowId');
  
  const [nodes, setNodes] = useState<Node<FlowNodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<Node<FlowNodeData> | null>(null);
  const [showNodeConfig, setShowNodeConfig] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [showEdgeConfig, setShowEdgeConfig] = useState(false);
  const [phoneGroups, setPhoneGroups] = useState<PhoneGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [availableLabels, setAvailableLabels] = useState(DUMMY_LABELS); // Will be fetched dynamically
  const [edgeInputLabel, setEdgeInputLabel] = useState<string>('');
  const [edgeOutputNumber, setEdgeOutputNumber] = useState<string>('');
  const [flowName, setFlowName] = useState<string>('');
  const [flowId, setFlowId] = useState<string>(editingFlowId || `flow_${Date.now()}`);
  const [botSettings, setBotSettings] = useState({
    enabled: false,
    name: '',
    description: '',
  });
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState({
    templates: true,
    otherActions: true,
    botSettings: true,
    assignGroups: true,
    selectGroup: true,
  });
  const [sidebarVisible, setSidebarVisible] = useState(true);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!wabaId) {
        setLoading(false);
        return;
      }

      try {
        const data = await getTemplatesList(wabaId, 100);
        const templateList = data?.data || [];
        setTemplates(templateList.filter((t: Template) => t.status === 'APPROVED'));
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [wabaId]);


  // Generate edge label based on source and target nodes
  const generateEdgeLabel = (sourceNode: Node<FlowNodeData> | undefined, targetNode: Node<FlowNodeData> | undefined, sourceHandle?: string | null, customLabel?: string): string => {
    // If custom label is provided, use it
    if (customLabel) {
      return customLabel;
    }

    if (!sourceNode || !targetNode) return '';

    const sourceType = sourceNode.data.type;
    const targetType = targetNode.data.type;

    // If source is a template with output labels, use the label from the sourceHandle
    if (sourceType === 'template' && sourceHandle && sourceNode.data.outputLabels) {
      const handleIndex = parseInt(sourceHandle.replace('output-', '')) || 0;
      if (sourceNode.data.outputLabels[handleIndex]) {
        return sourceNode.data.outputLabels[handleIndex];
      }
    }

    // If source is a template, show its outputs (variables)
    if (sourceType === 'template' && sourceNode.data.variables) {
      const vars = Object.keys(sourceNode.data.variables);
      if (vars.length > 0) {
        const varList = vars.map(v => `{{${v}}}`).join(', ');
        return `Output: ${varList}`;
      }
      return '';
    }

    // If target is a template, show what inputs it needs
    if (targetType === 'template' && targetNode.data.variables) {
      const vars = Object.keys(targetNode.data.variables);
      if (vars.length > 0) {
        const varList = vars.map(v => `{{${v}}}`).join(', ');
        return `Input: ${varList}`;
      }
      return '';
    }

    // Template to template connection - show data flow
    if (sourceType === 'template' && targetType === 'template') {
      const sourceVars = sourceNode.data.variables ? Object.keys(sourceNode.data.variables) : [];
      const targetVars = targetNode.data.variables ? Object.keys(targetNode.data.variables) : [];
      if (sourceVars.length > 0 && targetVars.length > 0) {
        return `Data: {{${sourceVars.join('}}, {{')}} → {{${targetVars.join('}}, {{')}}`;
      }
    }

    // For text nodes
    if (sourceType === 'text') {
      return 'Text message';
    }

    // For create ticket
    if (sourceType === 'create_ticket') {
      return 'Ticket created';
    }

    // For add to group
    if (sourceType === 'add_to_group') {
      return `Add to ${sourceNode.data.selectedGroupName || 'group'}`;
    }

    // For wait reply
    if (sourceType === 'wait_reply') {
      if (sourceHandle === 'yes') {
        return 'Yes →';
      } else if (sourceHandle === 'no') {
        return 'No →';
      }
      return 'Reply received';
    }

    // Default - empty label, user can add custom label
    return '';
  };

  const onConnect = useCallback((connection: Connection) => {
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);
    
    const label = generateEdgeLabel(sourceNode, targetNode, connection.sourceHandle || null);
    
    const newEdge: Edge = {
      ...connection,
      id: `edge-${connection.source}-${connection.target}-${Date.now()}`,
      label: label,
      labelStyle: { fill: '#4b5563', fontWeight: 500, fontSize: '11px' },
      labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
      style: { stroke: '#6b7280', strokeWidth: 2 },
      data: { customLabel: label || '' }, // Store label as custom label for saving
    };
    
    setEdges((eds) => [...eds, newEdge]);
  }, [nodes]);

  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.stopPropagation();
    setSelectedEdge(edge);
    setShowEdgeConfig(true);
    setSelectedNode(null);
    setShowNodeConfig(false);
    
    // Parse existing label to extract input and output parts
    const currentLabel = (edge.data as any)?.customLabel || edge.label || '';
    if (currentLabel && currentLabel.includes('-')) {
      const parts = currentLabel.split('-');
      const inputPart = parts.slice(0, -1).join('-'); // Everything before last "-"
      const outputPart = parts[parts.length - 1]; // Last part after "-"
      setEdgeInputLabel(inputPart);
      setEdgeOutputNumber(outputPart);
    } else {
      setEdgeInputLabel('');
      setEdgeOutputNumber('');
    }
  }, []);

  const updateEdge = (edgeId: string, updates: Partial<Edge>) => {
    setEdges((eds) =>
      eds.map((edge) => {
        if (edge.id === edgeId) {
          const updated = { ...edge, ...updates };
          // Regenerate label if custom label is not set
          const customLabel = (updated.data as any)?.customLabel;
          if (!customLabel) {
            const sourceNode = nodes.find(n => n.id === edge.source);
            const targetNode = nodes.find(n => n.id === edge.target);
            updated.label = generateEdgeLabel(sourceNode, targetNode, edge.sourceHandle || null);
          } else {
            updated.label = customLabel;
          }
          setSelectedEdge(updated);
          console.log('updated-updated', updated);
          return updated;
        }
        console.log('updated-edge', edge);
        return edge;
      })
    );
  };

  const onNodesChange: OnNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => {
      const updated = applyNodeChanges(changes, nds) as Node<FlowNodeData>[];
      // Handle selection changes
      changes.forEach((change) => {
        if (change.type === 'select' && change.selected) {
          const node = updated.find((n) => n.id === change.id);
          if (node) {
            setSelectedNode(node);
            setShowNodeConfig(true);
          }
        }
      });
      return updated;
    });
  }, []);

  const onEdgesChange: OnEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Fetch phone groups
  const fetchPhoneGroups = async () => {
    if (!wabaId) return;
    
    setGroupsLoading(true);
    try {
      const response = await getPhoneGroups(wabaId);
      const groupsData = response?.data?.groups || [];
      const transformedGroups: PhoneGroup[] = groupsData.map((group: any) => ({
        id: group.groupId || group.id || `group_${Date.now()}`,
        name: group.groupName || group.name || 'Unnamed Group',
        description: group.description || '',
        totalCount: group.phoneNumberCount || group.totalCount || group.phoneNumbers?.length || 0
      }));
      setPhoneGroups(transformedGroups);
    } catch (error) {
      console.error('Error fetching phone groups:', error);
    } finally {
      setGroupsLoading(false);
    }
  };

  // Fetch groups on component mount
  useEffect(() => {
    if (wabaId) {
      fetchPhoneGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wabaId]);

  const addNode = async (type: 'template' | 'text' | 'wait_reply' | 'end' | 'create_ticket' | 'add_to_group' | 'webhook' | 'http_request', template?: Template, group?: PhoneGroup) => {
    const newNodeId = `${type}_${Date.now()}`;
    
    const startNode = nodes.find(n => n.id === 'start');
    
    // For templates, position at the top and fetch template counts from API
    let inputCount = 1;
    let outputCount = 1;
    let inputLabels: string[] = [];
    let outputLabels: string[] = [];
    
    if (template && type === 'template') {
      try {
        // Call API to get template counts and labels
        const templateCounts = await getTemplateCounts(template.id);
        if (templateCounts?.config) {
          inputCount = templateCounts.config.inputCount || 1;
          outputCount = templateCounts.config.outputCount || 1;
          inputLabels = templateCounts.config.inputLabels || [];
          outputLabels = templateCounts.config.outputLabels || [];
        }
      } catch (error) {
        console.error('Error fetching template counts:', error);
        // Fallback to defaults if API fails
      }
    }
    
    // Position template nodes at the top, other nodes to the right
    let position = { x: 400, y: 250 };
    if (type === 'template') {
      // Position templates at the top, spaced horizontally
      const templateNodes = nodes.filter(n => n.data.type === 'template');
      position = { x: 100 + (templateNodes.length * 280), y: 100 };
    } else {
      const lastNode = nodes.length > 0 ? nodes[nodes.length - 1] : null;
    const referenceNode = lastNode || startNode;
      position = referenceNode 
        ? { x: referenceNode.position.x + 300, y: referenceNode.position.y }
        : { x: 400, y: 250 };
    }
    
    // If template is provided, configure it immediately
    const variables = template ? getTemplateVariables(template) : [];
    
    const newNode: Node<FlowNodeData> = {
      id: newNodeId,
      type,
      position,
      data: {
        label: type === 'template' ? 'Send Template' : 
               type === 'text' ? 'Send Text' :
               type === 'wait_reply' ? 'Wait for Reply' :
               type === 'create_ticket' ? 'Create Ticket' :
               type === 'add_to_group' ? (group ? `Add to ${group.name}` : 'Add to Group') :
               type === 'webhook' ? 'Webhook' :
               type === 'http_request' ? 'HTTP Request' : 'End',
        type,
        ...(template && {
          templateId: template.id,
          templateName: template.name,
          variables: variables.reduce((acc, v) => ({ ...acc, [v]: '' }), {}),
          inputCount,
          outputCount,
          inputLabels,
          outputLabels,
        }),
        ...(group && {
          selectedGroupId: group.id,
          selectedGroupName: group.name,
        }),
        ...(type === 'webhook' && {
          inputCount: 1,
          outputCount: 1,
          webhookUrl: '',
        }),
        ...(type === 'http_request' && {
          inputCount: 1,
          outputCount: 1,
          httpMethod: 'GET',
          httpUrl: '',
          httpHeaders: '{}',
          httpBody: '',
        }),
      },
    };

    setNodes((nds) => [...nds, newNode]);
    
    // Auto-connect to Start node if it's the first node after Start
    if (startNode && nodes.length === 1) {
      setTimeout(() => {
        const sourceNode = nodes.find(n => n.id === 'start');
        const targetNode = { ...newNode, id: newNodeId };
        const label = generateEdgeLabel(sourceNode, targetNode, null);
        
        const newEdge: Edge = {
          id: `edge-start-${newNodeId}-${Date.now()}`,
          source: 'start',
          target: newNodeId,
          sourceHandle: null,
          targetHandle: null,
          label: label,
          labelStyle: { fill: '#4b5563', fontWeight: 500, fontSize: '11px' },
          labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
          style: { stroke: '#6b7280', strokeWidth: 2 },
        };
        
        setEdges((eds) => [...eds, newEdge]);
      }, 100);
    }
    
    // If template or group was provided, don't show config (already configured)
    // Otherwise, show config panel
    if (template || group) {
      setSelectedNode(newNode);
      setShowNodeConfig(false);
    } else {
      setSelectedNode(newNode);
      setShowNodeConfig(true);
    }
  };

  const updateNode = (nodeId: string, updates: Partial<FlowNodeData>) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          const updated = { ...node, data: { ...node.data, ...updates } };
          setSelectedNode(updated);
          
          // Update edge labels that connect from or to this node (only if no custom label)
          setEdges((eds) =>
            eds.map((edge) => {
              if (edge.source === nodeId || edge.target === nodeId) {
                // Only update if there's no custom label
                if (!edge.data?.customLabel) {
                  const sourceNode = edge.source === nodeId ? updated : nds.find(n => n.id === edge.source);
                  const targetNode = edge.target === nodeId ? updated : nds.find(n => n.id === edge.target);
                  const newLabel = generateEdgeLabel(sourceNode, targetNode, edge.sourceHandle || null);
                  return { ...edge, label: newLabel };
                }
              }
              return edge;
            })
          );
          
          return updated;
        }
        return node;
      })
    );
  };

  const getTemplateVariables = (template: Template): string[] => {
    const variables: string[] = [];
    template.components?.forEach((comp) => {
      if (comp.text) {
        const matches = comp.text.match(/\{\{(\d+)\}\}/g);
        if (matches) {
          matches.forEach((match) => {
            const num = match.replace(/\{\{|\}\}/g, '');
            if (!variables.includes(num)) {
              variables.push(num);
            }
          });
        }
      }
    });
    return variables.sort((a, b) => parseInt(a) - parseInt(b));
  };

  const compileToFSM = () => {
    const nextMap = new Map<string, Record<string, any>>();
    const inputMap = new Map<string, any[]>();
    
    // Process edges with labels
    edges.forEach((edge) => {
      const key = edge.sourceHandle || 'next';
      const source = edge.source;
      const target = edge.target;
      
      // Get source and target nodes to access labels
      const sourceNode = nodes.find(n => n.id === source);
      const targetNode = nodes.find(n => n.id === target);
      const sourceOutputLabels = sourceNode?.data?.outputLabels || [];
      const targetInputLabels = targetNode?.data?.inputLabels || [];
      
      // Determine edge label priority:
      // 1. Custom label from edge data (user-edited)
      // 2. Edge label (from UI display)
      // 3. Output label from source node based on sourceHandle
      // 4. Input label from target node based on targetHandle
      let edgeLabel = '';
      
      // First priority: custom label (user-edited)
      if (edge.data?.customLabel) {
        edgeLabel = String((edge.data as any).customLabel || '');
      }
      // Second priority: edge label (from UI)
      else if (edge.label) {
        edgeLabel = typeof edge.label === 'string' ? edge.label : String(edge.label);
      }
      // Third priority: output label from source node
      else if (sourceNode && edge.sourceHandle && sourceOutputLabels.length > 0) {
        // Extract index from sourceHandle (e.g., "output-0" -> 0, "output-1" -> 1)
        const handleIndex = parseInt(edge.sourceHandle.replace('output-', '').replace('input-', '')) || 0;
        if (sourceOutputLabels[handleIndex]) {
          edgeLabel = sourceOutputLabels[handleIndex];
        }
      }
      // Fourth priority: input label from target node
      else if (targetNode && edge.targetHandle && targetInputLabels.length > 0) {
        // Extract index from targetHandle (e.g., "input-0" -> 0)
        const handleIndex = parseInt(edge.targetHandle.replace('input-', '').replace('output-', '')) || 0;
        if (targetInputLabels[handleIndex]) {
          edgeLabel = targetInputLabels[handleIndex];
        }
      }
      
      // Build output transitions
      if (!nextMap.has(source)) {
        nextMap.set(source, {});
      }
      const transitions = nextMap.get(source)!;
      transitions[key] = {
        target,
        label: edgeLabel,
        sourceHandle: edge.sourceHandle || null,
        targetHandle: edge.targetHandle || null,
      };
      
      // Build input connections
      if (!inputMap.has(target)) {
        inputMap.set(target, []);
      }
      const inputs = inputMap.get(target)!;
      inputs.push({
        source,
        label: edgeLabel,
        sourceHandle: edge.sourceHandle || null,
        targetHandle: edge.targetHandle || null,
      });
    });

    return nodes.map((node) => {
      const nodeData = node.data;
      const transitions = nextMap.get(node.id) || {};
      const inputs = inputMap.get(node.id) || [];
      
      return {
        id: node.id,
        kind: nodeData.type,
        config: {
          ...(nodeData.templateId && { templateId: nodeData.templateId }),
          ...(nodeData.templateName && { templateName: nodeData.templateName }),
          ...(nodeData.variables && { variables: nodeData.variables }),
          ...(nodeData.textContent && { textContent: nodeData.textContent }),
          ...(nodeData.yesTemplate && { yesTemplate: nodeData.yesTemplate }),
          ...(nodeData.noTemplate && { noTemplate: nodeData.noTemplate }),
          ...(nodeData.selectedGroupId && { selectedGroupId: nodeData.selectedGroupId }),
          ...(nodeData.selectedGroupName && { selectedGroupName: nodeData.selectedGroupName }),
          ...(nodeData.inputCount && { inputCount: nodeData.inputCount }),
          ...(nodeData.outputCount && { outputCount: nodeData.outputCount }),
          ...(nodeData.inputLabels && nodeData.inputLabels.length > 0 && { inputLabels: nodeData.inputLabels }),
          ...(nodeData.outputLabels && nodeData.outputLabels.length > 0 && { outputLabels: nodeData.outputLabels }),
          ...(nodeData.webhookUrl && { webhookUrl: nodeData.webhookUrl }),
          ...(nodeData.httpMethod && { httpMethod: nodeData.httpMethod }),
          ...(nodeData.httpUrl && { httpUrl: nodeData.httpUrl }),
          ...(nodeData.httpHeaders && { httpHeaders: nodeData.httpHeaders }),
          ...(nodeData.httpBody && { httpBody: nodeData.httpBody }),
        },
        inputs: inputs.length > 0 ? inputs : undefined,
        transitions: Object.keys(transitions).length > 0 ? transitions : undefined,
      };
    });
  };

  // Load flow from saved FSM
  const loadFlowFromFSM = (fsm: any[]) => {
    const loadedNodes: Node<FlowNodeData>[] = [];
    const loadedEdges: Edge[] = [];
    let xPosition = 100;
    const yPosition = 250;
    let templateIndex = 0;

    fsm.forEach((fsmNode, index) => {
      // Create node based on FSM data
      let nodeData: FlowNodeData = {
        label: fsmNode.kind === 'start' ? 'Start' : 
               fsmNode.kind === 'template' ? (fsmNode.config.templateName || 'Send Template') :
               fsmNode.kind === 'text' ? 'Send Text' :
               fsmNode.kind === 'wait_reply' ? 'Wait for Reply' :
               fsmNode.kind === 'create_ticket' ? 'Create Ticket' :
               fsmNode.kind === 'add_to_group' ? (fsmNode.config.selectedGroupName ? `Add to ${fsmNode.config.selectedGroupName}` : 'Add to Group') :
               fsmNode.kind === 'webhook' ? 'Webhook' :
               fsmNode.kind === 'http_request' ? 'HTTP Request' : 'End',
        type: fsmNode.kind as any,
      };

      // Add template-specific data
      if (fsmNode.kind === 'template') {
        nodeData = {
          ...nodeData,
          templateId: fsmNode.config.templateId,
          templateName: fsmNode.config.templateName,
          variables: fsmNode.config.variables || {},
          inputCount: fsmNode.config.inputCount || 1,
          outputCount: fsmNode.config.outputCount || 1,
          inputLabels: fsmNode.config.inputLabels || [],
          outputLabels: fsmNode.config.outputLabels || [],
        };
      }

      // Add other node-specific data
      if (fsmNode.kind === 'text') {
        nodeData.textContent = fsmNode.config.textContent;
      }
      if (fsmNode.kind === 'wait_reply') {
        nodeData.yesTemplate = fsmNode.config.yesTemplate;
        nodeData.noTemplate = fsmNode.config.noTemplate;
      }
      if (fsmNode.kind === 'add_to_group') {
        nodeData.selectedGroupId = fsmNode.config.selectedGroupId;
        nodeData.selectedGroupName = fsmNode.config.selectedGroupName;
      }
      if (fsmNode.kind === 'webhook') {
        nodeData.webhookUrl = fsmNode.config.webhookUrl || '';
        nodeData.inputCount = fsmNode.config.inputCount || 1;
        nodeData.outputCount = fsmNode.config.outputCount || 1;
      }
      if (fsmNode.kind === 'http_request') {
        nodeData.httpMethod = fsmNode.config.httpMethod || 'GET';
        nodeData.httpUrl = fsmNode.config.httpUrl || '';
        nodeData.httpHeaders = fsmNode.config.httpHeaders || '{}';
        nodeData.httpBody = fsmNode.config.httpBody || '';
        nodeData.inputCount = fsmNode.config.inputCount || 1;
        nodeData.outputCount = fsmNode.config.outputCount || 1;
      }

      // Position template nodes at the top, other nodes to the right
      let position = { x: xPosition + (index * 300), y: yPosition };
      if (fsmNode.kind === 'template') {
        position = { x: 100 + (templateIndex * 280), y: 100 };
        templateIndex++;
      } else if (fsmNode.kind !== 'start') {
        // Position non-template, non-start nodes to the right
        const nonTemplateIndex = index - templateIndex;
        position = { x: xPosition + (nonTemplateIndex * 300), y: yPosition };
      }

      const node: Node<FlowNodeData> = {
        id: fsmNode.id,
        type: fsmNode.kind === 'start' ? 'start' : fsmNode.kind,
        position,
        data: nodeData,
      };

      loadedNodes.push(node);

      // Create edges from transitions
      if (fsmNode.transitions) {
        Object.entries(fsmNode.transitions).forEach(([key, transition]: [string, any]) => {
          const edge: Edge = {
            id: `edge-${fsmNode.id}-${transition.target}-${Date.now()}-${key}`,
            source: fsmNode.id,
            target: transition.target,
            sourceHandle: transition.sourceHandle || null,
            targetHandle: transition.targetHandle || null,
            label: transition.label || '',
            labelStyle: { fill: '#4b5563', fontWeight: 500, fontSize: '11px' },
            labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
            style: { stroke: '#6b7280', strokeWidth: 2 },
            data: { customLabel: transition.label || '' },
          };
          loadedEdges.push(edge);
        });
      }
    });

    setNodes(loadedNodes);
    setEdges(loadedEdges);
  };

  const saveFlow = async () => {
    if (!wabaId) {
      alert('WABA ID not found. Please log in again.');
      return;
    }
    
    if (!flowName.trim()) {
      alert('Please enter a flow name.');
      return;
    }
    
    const fsm = compileToFSM();
    
    // Create the structure with flow_settings, bot_settings, assign_groups, and wabaId
    const flowDataToSave: any = {
      wabaId: wabaId,
      flow_settings: [
        ...fsm,
        {
          "id": "flow_config",
          "kind": "config",
          "config": {
            "botSettings": botSettings,
            "assignedGroups": selectedGroupIds,
            "metadata": {
              "name": flowName.trim(),
              "createdAt": flowId === 'default' ? Date.now() : parseInt(flowId.split('_')[1]) || Date.now(),
              "lastModified": Date.now(),
            }
          }
        }
      ],
      bot_settings: botSettings,
      assign_groups: selectedGroupIds
    };
    
    // Add flowId if editing existing flow
    if (editingFlowId) {
      flowDataToSave.flowId = editingFlowId;
    }
    
    console.log('Complete Flow Data to Save:', JSON.stringify(flowDataToSave, null, 2));
    console.log('FSM Array:', JSON.stringify(fsm, null, 2));
    console.log('Bot Settings:', botSettings);
    console.log('Assigned Groups:', selectedGroupIds);
    
    try {
      // Refresh token before saving
      console.log('Refreshing token before saving flow...');
      const newToken = await refreshToken();
      if (!newToken) {
        console.warn('Token refresh failed or no refresh token available, proceeding with current token');
      } else {
        console.log('Token refreshed successfully');
      }
      
      // Save to API
      await saveFlowAPI(flowDataToSave);
      
      // Also save to localStorage as backup
    const flowKey = flowId === 'default' 
      ? `automated_flow_${wabaId}`
      : `automated_flow_${wabaId}_${flowId}`;
      localStorage.setItem(flowKey, JSON.stringify(flowDataToSave.flow_settings));
    
      // Save metadata separately for backward compatibility
    const metadataKey = `flow_metadata_${wabaId}_${flowId}`;
    const metadata = {
      name: flowName.trim(),
      createdAt: flowId === 'default' ? Date.now() : parseInt(flowId.split('_')[1]) || Date.now(),
      lastModified: Date.now(),
    };
    localStorage.setItem(metadataKey, JSON.stringify(metadata));
    
    alert('Flow saved successfully!');
    } catch (error: any) {
      console.error('Error saving flow:', error);
      alert(`Error saving flow: ${error.message || 'Please try again'}`);
      return;
    }
    
    // Navigate back to flows list without page refresh
    setTimeout(() => {
      navigate('/events/automated-flows-list');
    }, 1000);
  };

  // Load flow from API when editing
  useEffect(() => {
    const loadFlowForEditing = async () => {
      if (!wabaId || !editingFlowId || loading || nodes.length > 0) return;
      
      try {
        setLoading(true);
        
        // Fetch flow from API
        const response = await getFlowById(editingFlowId);
        const flowData = response?.data || response;
        
        if (!flowData) {
          console.error('Flow not found');
          // Initialize with start node if flow not found
          setNodes([
            {
              id: 'start',
              type: 'start',
              position: { x: 100, y: 250 },
              data: { label: 'Start', type: 'start' },
            },
          ]);
          return;
        }
        
        // Set flow ID and name
        setFlowId(editingFlowId);
        setFlowName(flowData.flowName || flowData.name || '');
        
        // Load bot settings and assigned groups
        if (flowData.bot_settings) {
          setBotSettings(flowData.bot_settings);
        }
        if (flowData.assign_groups) {
          setSelectedGroupIds(flowData.assign_groups);
        }
        
        // Load flow structure from flow_settings
        const flowSettings = flowData.flow_settings || [];
        if (flowSettings.length > 0) {
          // Filter out config node before loading
          const fsmNodes = flowSettings.filter((node: any) => node.kind !== 'config');
          loadFlowFromFSM(fsmNodes);
        } else {
          // Initialize with start node if no flow settings
          setNodes([
            {
              id: 'start',
              type: 'start',
              position: { x: 100, y: 250 },
              data: { label: 'Start', type: 'start' },
            },
          ]);
        }
      } catch (error: any) {
        console.error('Error loading flow for editing:', error);
        // Initialize with start node on error
        setNodes([
          {
            id: 'start',
            type: 'start',
            position: { x: 100, y: 250 },
            data: { label: 'Start', type: 'start' },
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    loadFlowForEditing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wabaId, editingFlowId, loading, templates.length]);

  // Initialize with start node if not editing
  useEffect(() => {
    if (wabaId && !loading && nodes.length === 0 && !editingFlowId) {
      setFlowId('default');
      setNodes([
        {
          id: 'start',
          type: 'start',
          position: { x: 100, y: 250 },
          data: { label: 'Start', type: 'start' },
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wabaId, loading, editingFlowId]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading templates...</div>
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`${sidebarVisible ? 'w-80' : 'w-0'} bg-white border-r border-gray-200 transition-all duration-300 overflow-hidden`}>
        {sidebarVisible && (
          <div className="p-4 overflow-y-auto h-full">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">Flow Builder</h2>
              <button
                onClick={() => setSidebarVisible(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="Hide sidebar"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Flow Name *
            </label>
            <input
              type="text"
              value={flowName}
              onChange={(e) => setFlowName(e.target.value)}
              placeholder="Enter flow name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
        </div>
        
        {/* Templates List */}
        <div className="mb-6">
          <button
            onClick={() => setExpandedSections({ ...expandedSections, templates: !expandedSections.templates })}
            className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 mb-3 hover:text-gray-900"
          >
            <span>Select Template</span>
            <svg
              className={`w-4 h-4 transition-transform ${expandedSections.templates ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expandedSections.templates && (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {loading ? (
              <div className="text-sm text-gray-500 px-2">Loading templates...</div>
            ) : templates.length === 0 ? (
              <div className="text-sm text-gray-500 px-2">No templates available</div>
            ) : (
              templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => addNode('template', template)}
                  className="w-full px-3 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-left transition-all shadow-sm hover:shadow"
                >
                  <div className="text-sm font-medium text-gray-900">{template.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{template.category}</div>
                </button>
              ))
            )}
          </div>
          )}
        </div>

        {/* Other Node Buttons */}
        <div className="space-y-2 mb-6 border-t border-gray-200 pt-4">
          <button
            onClick={() => setExpandedSections({ ...expandedSections, otherActions: !expandedSections.otherActions })}
            className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 mb-3 hover:text-gray-900"
          >
            <span>Other Actions</span>
            <svg
              className={`w-4 h-4 transition-transform ${expandedSections.otherActions ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expandedSections.otherActions && (
            <div className="space-y-2">
          <button
            onClick={() => addNode('text')}
            className="w-full px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium transition-all shadow-sm hover:shadow"
          >
            + Send Text
          </button>
          <button
            onClick={() => addNode('create_ticket')}
            className="w-full px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium transition-all shadow-sm hover:shadow"
          >
            + Create Ticket in CRM
          </button>
          <button
            onClick={() => addNode('webhook')}
            className="w-full px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium transition-all shadow-sm hover:shadow"
          >
            + Webhook
          </button>
          <button
            onClick={() => addNode('http_request')}
            className="w-full px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium transition-all shadow-sm hover:shadow"
          >
            + HTTP Request
          </button>
            </div>
          )}
        </div>

        {/* Bot Settings */}
        <div className="mb-6 border-t border-gray-200 pt-4">
          <button
            onClick={() => setExpandedSections({ ...expandedSections, botSettings: !expandedSections.botSettings })}
            className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 mb-3 hover:text-gray-900"
          >
            <span>Assign Bot Settings</span>
            <svg
              className={`w-4 h-4 transition-transform ${expandedSections.botSettings ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expandedSections.botSettings && (
            <div className="space-y-3">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={botSettings.enabled}
                  onChange={(e) => setBotSettings({ ...botSettings, enabled: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Enable Bot</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bot Name</label>
              <input
                type="text"
                value={botSettings.name}
                onChange={(e) => setBotSettings({ ...botSettings, name: e.target.value })}
                placeholder="Enter bot name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={botSettings.description}
                onChange={(e) => setBotSettings({ ...botSettings, description: e.target.value })}
                placeholder="Enter bot description"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            </div>
          )}
        </div>

        {/* Assign Groups */}
        <div className="mb-6 border-t border-gray-200 pt-4">
          <button
            onClick={() => setExpandedSections({ ...expandedSections, assignGroups: !expandedSections.assignGroups })}
            className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 mb-3 hover:text-gray-900"
          >
            <span>Assign Groups</span>
            <svg
              className={`w-4 h-4 transition-transform ${expandedSections.assignGroups ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expandedSections.assignGroups && (
            <>
              {groupsLoading ? (
                <div className="text-sm text-gray-500 px-2">Loading groups...</div>
              ) : phoneGroups.length === 0 ? (
                <div className="text-sm text-gray-500 px-2">No groups available</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
              {phoneGroups.map((group) => (
                <label
                  key={group.id}
                  className="flex items-start space-x-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.includes(group.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedGroupIds([...selectedGroupIds, group.id]);
                      } else {
                        setSelectedGroupIds(selectedGroupIds.filter(id => id !== group.id));
                      }
                    }}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{group.name}</div>
                    {group.totalCount !== undefined && (
                      <div className="text-xs text-gray-500">
                        {group.totalCount} phone number{group.totalCount !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </label>
              ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Phone Groups List - For adding nodes */}
        <div className="mb-6 border-t border-gray-200 pt-4">
          <button
            onClick={() => setExpandedSections({ ...expandedSections, selectGroup: !expandedSections.selectGroup })}
            className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 mb-3 hover:text-gray-900"
          >
            <span>Select Group (Add Node)</span>
            <svg
              className={`w-4 h-4 transition-transform ${expandedSections.selectGroup ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expandedSections.selectGroup && (
            <>
          {groupsLoading ? (
            <div className="text-sm text-gray-500 px-2">Loading groups...</div>
          ) : phoneGroups.length === 0 ? (
            <div className="text-sm text-gray-500 px-2">No groups available</div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {phoneGroups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => addNode('add_to_group', undefined, group)}
                  className="w-full px-3 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-left transition-all shadow-sm hover:shadow"
                >
                  <div className="text-sm font-medium text-gray-900">{group.name}</div>
                  {group.totalCount !== undefined && (
                    <div className="text-xs text-gray-500">
                      {group.totalCount} phone number{group.totalCount !== 1 ? 's' : ''}
                    </div>
                  )}
                </button>
              ))}
            </div>
              )}
            </>
          )}
        </div>

        {/* Node Configuration */}
        {showNodeConfig && selectedNode && (
          <div className="border-t border-gray-200 pt-4">
            
            {selectedNode.data.type === 'template' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Template</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700">
                    {selectedNode.data.templateName || 'No template selected'}
                </div>
                    </div>
              </div>
            )}

            {selectedNode.data.type === 'text' && (
              <div>
                <label className="block text-sm font-medium mb-1">Text Message (max 250 chars)</label>
                <textarea
                  value={selectedNode.data.textContent || ''}
                  onChange={(e) => {
                    const text = e.target.value.substring(0, 250);
                    updateNode(selectedNode.id, { textContent: text });
                  }}
                  maxLength={250}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Enter your message..."
                />
                <div className="text-xs text-gray-500 mt-1">
                  {(selectedNode.data.textContent?.length || 0)}/250 characters
                </div>
              </div>
            )}

            {selectedNode.data.type === 'create_ticket' && (
              <div className="space-y-3">
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-sm text-indigo-800">
                    This node will create a ticket in the CRM system when reached in the flow.
                  </p>
                </div>
              </div>
            )}


            {selectedNode.data.type === 'webhook' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Webhook URL</label>
                  <input
                    type="text"
                    value={selectedNode.data.webhookUrl || ''}
                    onChange={(e) => {
                      updateNode(selectedNode.id, { webhookUrl: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="https://example.com/webhook"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Input Connections</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={selectedNode.data.inputCount || 1}
                      onChange={(e) => {
                        const count = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                        updateNode(selectedNode.id, { inputCount: count });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <div className="text-xs text-gray-500 mt-1">Number of input handles</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Output Connections</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={selectedNode.data.outputCount || 1}
                      onChange={(e) => {
                        const count = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                        updateNode(selectedNode.id, { outputCount: count });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <div className="text-xs text-gray-500 mt-1">Number of output handles</div>
                  </div>
                </div>
              </div>
            )}

            {selectedNode.data.type === 'http_request' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">HTTP Method</label>
                  <select
                    value={selectedNode.data.httpMethod || 'GET'}
                    onChange={(e) => {
                      updateNode(selectedNode.id, { httpMethod: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="PATCH">PATCH</option>
                    <option value="DELETE">DELETE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">URL</label>
                  <input
                    type="text"
                    value={selectedNode.data.httpUrl || ''}
                    onChange={(e) => {
                      updateNode(selectedNode.id, { httpUrl: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="https://api.example.com/endpoint"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Headers (JSON)</label>
                  <textarea
                    value={selectedNode.data.httpHeaders || '{}'}
                    onChange={(e) => {
                      try {
                        JSON.parse(e.target.value); // Validate JSON
                        updateNode(selectedNode.id, { httpHeaders: e.target.value });
                      } catch (err) {
                        // Allow invalid JSON while typing
                        updateNode(selectedNode.id, { httpHeaders: e.target.value });
                      }
                    }}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    placeholder='{"Content-Type": "application/json"}'
                  />
                  <div className="text-xs text-gray-500 mt-1">Enter headers as JSON object</div>
                </div>
                {(selectedNode.data.httpMethod === 'POST' || selectedNode.data.httpMethod === 'PUT' || selectedNode.data.httpMethod === 'PATCH') && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Request Body</label>
                    <textarea
                      value={selectedNode.data.httpBody || ''}
                      onChange={(e) => {
                        updateNode(selectedNode.id, { httpBody: e.target.value });
                      }}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                      placeholder='{"key": "value"}'
                    />
                    <div className="text-xs text-gray-500 mt-1">Enter request body (JSON or text)</div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Input Connections</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={selectedNode.data.inputCount || 1}
                      onChange={(e) => {
                        const count = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                        updateNode(selectedNode.id, { inputCount: count });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <div className="text-xs text-gray-500 mt-1">Number of input handles</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Output Connections</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={selectedNode.data.outputCount || 1}
                      onChange={(e) => {
                        const count = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                        updateNode(selectedNode.id, { outputCount: count });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <div className="text-xs text-gray-500 mt-1">Number of output handles</div>
                  </div>
                </div>
              </div>
            )}

            {selectedNode.data.type === 'wait_reply' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">If "Yes" → Template</label>
                  <select
                    value={selectedNode.data.yesTemplate || ''}
                    onChange={(e) => {
                      updateNode(selectedNode.id, { yesTemplate: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Choose template...</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">If "No" → Template</label>
                  <select
                    value={selectedNode.data.noTemplate || ''}
                    onChange={(e) => {
                      updateNode(selectedNode.id, { noTemplate: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Choose template...</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edge Configuration */}
        {showEdgeConfig && selectedEdge && (
          <div className="border-t border-gray-200 pt-4">
            <div className="space-y-3">
              <div className="text-xs text-gray-500">
                <div>From: {nodes.find(n => n.id === selectedEdge.source)?.data.label || selectedEdge.source}</div>
                <div>To: {nodes.find(n => n.id === selectedEdge.target)?.data.label || selectedEdge.target}</div>
                {selectedEdge.label && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="text-xs text-gray-400 mb-1">Label:</div>
                    <div className="text-sm font-medium text-gray-700">{selectedEdge.label}</div>
              </div>
                )}
            </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <button
            onClick={saveFlow}
            className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold"
          >
            Save Flow
          </button>
        </div>
          </div>
        )}
      </div>

      {/* Flow Canvas */}
      <div className="flex-1 relative">
        {!sidebarVisible && (
          <button
            onClick={() => setSidebarVisible(true)}
            className="absolute top-4 left-4 z-10 p-2 bg-white border border-gray-200 rounded-lg shadow-md hover:bg-gray-50 transition-colors"
            title="Show sidebar"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          attributionPosition="bottom-left"
        >
          <Background color="#e5e7eb" gap={16} size={1} />
          <Controls 
            showInteractive={false}
            style={{ 
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}
          />
        </ReactFlow>
      </div>
    </div>
    </ReactFlowProvider>
  );
}

